import { BEARER_PREFIX } from '@worklogix/shared';
import { useAuth } from '@worklogix/web-common';
import { useEffect, useState } from 'react';

import { photoProxyUrl } from './photoUrl';

/** Loading lifecycle of an authenticated punch-photo fetch. */
export type PunchPhotoState = 'idle' | 'loading' | 'ready' | 'error';

/** Result of {@link usePunchPhoto}: a blob object URL once ready, plus its state. */
export interface PunchPhotoResult {
  state: PunchPhotoState;
  /** Object URL for the fetched JPEG; defined only while `state === 'ready'`. */
  url: string | undefined;
}

/**
 * Fetch one punch's verification photo through the RLS-checked API proxy and
 * expose it as a blob object URL for an `<img>`.
 *
 * WHY A FETCH (not a bare `<img src>`): the proxy is bearer-gated (Entra
 * Manager/Admin + RLS, plan-v7 Decision 1 — "no bearer-capability URL to leak"),
 * and an `<img>` element cannot attach an `Authorization` header. So we fetch
 * with the acquired token, `cache: 'no-store'` (the server also sends
 * `Cache-Control: private, no-store`), and hand the `<img>` an object URL that
 * is revoked on cleanup. Nothing is persisted or logged.
 *
 * The fetch is gated by `enabled` so callers can defer it until the thumbnail is
 * actually visible / a blade is opened (lazy-load — no N requests on list render).
 */
export function usePunchPhoto(punchId: string | undefined, enabled: boolean): PunchPhotoResult {
  const { acquireToken } = useAuth();
  const [state, setState] = useState<PunchPhotoState>('idle');
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || punchId === undefined || punchId === '') {
      setState('idle');
      return;
    }

    let active = true;
    let objectUrl: string | undefined;
    setState('loading');
    setUrl(undefined);

    const load = async (): Promise<void> => {
      const token = await acquireToken();
      const response = await fetch(photoProxyUrl(import.meta.env.VITE_API_BASE, punchId), {
        headers: {
          Authorization: `${BEARER_PREFIX}${token}`,
          Accept: 'image/jpeg',
        },
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`photo proxy responded ${response.status}`);
      }
      const blob = await response.blob();
      if (!active) {
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      setState('ready');
    };

    load().catch(() => {
      if (active) {
        // Non-PII: we log nothing and surface only an opaque error state (a
        // missing photo, a 404 cross-tenant guard, or a transient failure all
        // collapse to the same "no image" placeholder).
        setState('error');
      }
    });

    return () => {
      active = false;
      if (objectUrl !== undefined) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [acquireToken, punchId, enabled]);

  return { state, url };
}
